import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Row, Col } from 'reactstrap';
import { SOURCES } from 'client/utils/image-helpers';
import { Image } from 'site-modules/shared/components/image/image';
import { formatPriceStringV2, getPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { LeaseDealsCta } from 'site-modules/lease-deals/components/lease-deals-cta/lease-deals-cta';
import { ShareButton } from 'site-modules/lease-deals/components/lease-deals-details-modal/share-button/share-button';

import './modal-header.scss';

export function ModalHeader({
  ctaRef,
  id,
  photo = undefined,
  errorImage = undefined,
  year,
  make,
  model,
  payment,
  effectivePayment,
  dueAtSigning,
  msrp,
  term,
  styleId,
  isShop4931ZeroDownRoute = false,
  isMobile = false,
  endDate,
  trim = undefined,
  disclaimer = undefined,
  webUrls = undefined,
  showCta = false,
}) {
  const displayedPayment = isShop4931ZeroDownRoute ? Math.round(effectivePayment) : payment;
  const displayedDueAtSigning = isShop4931ZeroDownRoute ? effectivePayment : dueAtSigning;
  const signingPrice = getPriceStringV2(displayedDueAtSigning, 'Contact dealer', { allowZero: true });
  const formattedMsrp = getPriceStringV2(msrp);

  return (
    <Row className="lease-deals-modal-header gx-2 align-items-center">
      <Col xs={12} md={5} className="mb-2 mb-md-0">
        <div className="pos-r">
          <Image
            lazy
            alt={photo?.title}
            source={SOURCES.MEDIA}
            errorImage={errorImage}
            imageId={photo?.href ?? ''}
            className="vehicle-image object-fit-cover w-100"
          />
          <ShareButton styleId={styleId} id={id} make={make} model={model} year={year} />
        </div>
      </Col>
      <Col xs={12} md={7}>
        <div className="d-flex flex-column gap-md-1">
          <h2 className="text-cool-gray-10 size-24 fw-bold mb-1 mb-md-0 text-center text-md-start">{`${year} ${make} ${model}`}</h2>
          <h3 className="payment-value mb-1 mb-md-0 fw-bold d-flex align-items-center gap-0_5 justify-content-center justify-content-md-start">
            {formatPriceStringV2(displayedPayment)}
            <small className="text-muted size-14 fw-normal">/mo*</small>
          </h3>
          <p className="mb-1 mb-md-0 text-cool-gray-40 mt-0 size-16 d-flex align-items-center flex-wrap column-gap-0_75 column-gap-md-1 row-gap-0_25 justify-content-center justify-content-md-start">
            <span>
              <strong className="text-cool-gray-30">{signingPrice}</strong> due at signing
            </span>
            <span aria-hidden>|</span>
            <span>
              <strong className="text-cool-gray-30">{term} months</strong> term
            </span>
            {!isMobile && <span aria-hidden>|</span>}
            <span className={classNames({ 'w-100 text-center': isMobile })}>
              MSRP <strong className="text-cool-gray-30">{formattedMsrp}</strong>
            </span>
          </p>
          <div ref={ctaRef} className="align-self-start align-self-md-auto w-100 text-md-start text-center">
            <LeaseDealsCta
              styleId={styleId}
              make={make}
              model={model}
              year={year}
              trim={trim}
              payment={payment}
              term={term}
              dueAtSigning={dueAtSigning}
              endDate={endDate}
              disclaimer={disclaimer}
              webUrls={webUrls}
              photo={photo}
              errorImage={errorImage}
              showCta={showCta}
              buttonProps={{
                className: 'deal-button text-transform-none px-1_5 text-white btn fw-medium size-16 rounded-1 py-0_75',
                color: 'blue-50',
              }}
              formName="oem_lease_deal_detail"
            />
          </div>
        </div>
      </Col>
    </Row>
  );
}

ModalHeader.propTypes = {
  ctaRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  id: PropTypes.string.isRequired,
  year: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  make: PropTypes.string.isRequired,
  model: PropTypes.string.isRequired,
  payment: PropTypes.number.isRequired,
  effectivePayment: PropTypes.number.isRequired,
  dueAtSigning: PropTypes.number.isRequired,
  msrp: PropTypes.number.isRequired,
  term: PropTypes.number.isRequired,
  styleId: PropTypes.number.isRequired,
  isShop4931ZeroDownRoute: PropTypes.bool,
  endDate: PropTypes.string.isRequired,
  photo: PropTypes.shape({}),
  errorImage: PropTypes.string,
  isMobile: PropTypes.bool,
  trim: PropTypes.string,
  disclaimer: PropTypes.string,
  webUrls: PropTypes.string,
  showCta: PropTypes.bool,
};
